import {useCallback, useEffect, useMemo, useState} from 'react';
import {Participant, Room, RoomEvent, setLogLevel, Track} from 'livekit-client';
import {getLiveKitRoomToken, useT, getCurrentTimezone} from '@kcast-fe/core';
import {APP_VARS, toastUtils} from 'core';

export type TLiveKitStatus = 'pending' | 'connected' | 'disconnected' | 'error';

export interface IUseLiveKitResult {
  room: Room;
  isPending: boolean;
  isConnected: boolean;
  isDisconnected: boolean;
  isError: boolean;
  cameraAmount: number;
  activeSpeakers: Participant[];
  connect: () => Promise<void>;
  toggleCamera: (enabled: boolean) => Promise<void>;
  switchCamera: () => Promise<void>;
  disconnect: () => Promise<void>;
}

export const useLiveKit = (): IUseLiveKitResult => {
  const [status, setStatus] = useState<TLiveKitStatus>('disconnected');
  const [activeSpeakers, setActiveSpeakers] = useState<Participant[]>([]);
  const [cameraAmount, setCameraAmount] = useState(0);

  const {t} = useT();

  const room = useMemo(() => new Room(), []);

  const isPending = useMemo(() => status === 'pending', [status]);
  const isConnected = useMemo(() => status === 'connected', [status]);
  const isDisconnected = useMemo(() => status === 'disconnected', [status]);
  const isError = useMemo(() => status === 'error', [status]);

  setLogLevel('error');

  useEffect(() => {
    const onMediaDevicesError = (error: Error) => {
      console.error('[LiveKit] Device error:', error);
      toastUtils.error({message: t('errors.device')});
    };

    const handleSpeakers = (speakers: Participant[]) => {
      setActiveSpeakers(speakers);
    };

    room.on(RoomEvent.ActiveSpeakersChanged, handleSpeakers);
    room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);

    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, handleSpeakers);
      room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
      room.disconnect(true).then();
    };
  }, [room, t]);

  const connect = useCallback(async () => {
    if (isConnected) {
      console.warn('[LiveKit] Already connected.');
      return;
    }

    console.info('[LiveKit] Connecting...');

    try {
      setStatus('pending');
      const token: string = await getLiveKitRoomToken({
        timezone: getCurrentTimezone(),
        client: 'web'
      });

      await Promise.all([
        room.localParticipant.setMicrophoneEnabled(true),
        room.connect(APP_VARS.LIVEKIT_URL, token)
      ]);

      setStatus('connected');
      console.info(`[LiveKit] Connected. Room: ${room.name}.`);
    } catch (error) {
      setStatus('disconnected');
      console.error('[LiveKit] Failed to connect:', error);
    }
  }, [isConnected, room]);

  const toggleCamera = useCallback(
    async (enabled: boolean) => {
      if (isConnected) {
        await room.localParticipant.setCameraEnabled(enabled);
        if (cameraAmount === 0) {
          const devices = await Room.getLocalDevices('videoinput');
          setCameraAmount(devices.length);
        }
      }
    },
    [cameraAmount, isConnected, room.localParticipant]
  );

  const switchCamera = useCallback(async () => {
    if (isConnected) {
      try {
        const devices = await Room.getLocalDevices('videoinput');
        if (devices.length > 1) {
          // Find the current device in use
          const currentTrack = room.localParticipant.getTrackPublication(
            Track.Source.Camera
          )?.track;
          const currentDeviceId = currentTrack?.mediaStreamTrack.getSettings().deviceId;

          // Pick the next device (toggle between two cameras)
          const nextDevice = devices.find((d) => d.deviceId !== currentDeviceId) ?? devices[0];
          await room.switchActiveDevice('videoinput', nextDevice.deviceId);
        } else {
          console.warn('[LiveKit] Only one camera available.');
        }
      } catch (error) {
        console.error('[LiveKit] Failed to switch camera:', error);
      }
    }
  }, [isConnected, room]);

  const disconnect = useCallback(async () => {
    if (!isConnected) {
      console.warn('[LiveKit] Not connected.');
      return;
    }

    try {
      await room.disconnect(true);
      setStatus('disconnected');
      console.info(`[LiveKit] Disconnected. Room: ${room.name}.`);
    } catch (error) {
      setStatus('error');
      console.error(`[LiveKit] Failed to disconnect. Room: ${room.name}. `, error);
    }
  }, [room, isConnected]);

  return {
    room,
    isError,
    isPending,
    isConnected,
    isDisconnected,
    activeSpeakers,
    cameraAmount,
    connect,
    toggleCamera,
    switchCamera,
    disconnect
  };
};
